import React, { useState, useEffect, useCallback } from "react";
import DevelopersPagination from "@shared/navbars/DevelopersNavbar/Pagination";
import { getUserInterests } from "@services/krentDeveloper";
import { formatDate, numberWithCommas } from "@utils/helpers";
import { PropertyInterest } from "@utils/constants";
import { useNavigate } from "react-router-dom";
import BookInspection from "./book-inspection";

const ITEMS_PER_PAGE = 10;

const Interests: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [interests, setInterests] = useState<PropertyInterest[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInterests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const abortController = new AbortController();
      const response = await getUserInterests(abortController.signal);
      setInterests(response.data);
      setTotalPages(Math.ceil(response.data.length / ITEMS_PER_PAGE));
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInterests();
  }, [fetchInterests]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentInterests = interests?.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <Header />
      <InterestList
        interests={currentInterests}
        refetchInterests={fetchInterests}
      />
      <DevelopersPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

const Header: React.FC = () => (
  <div className="my-10">
    <div className="w-max ml-auto">
      <a
        className="fw-bold text-white bg-primary rounded-10 py-3 px-3"
        href="/developer-listings"
      >
        + Add new interests
      </a>
    </div>
  </div>
);

interface InterestListProps {
  interests: PropertyInterest[];
  refetchInterests: () => void;
}

const InterestList: React.FC<InterestListProps> = ({
  interests,
  refetchInterests,
}) => (
  <div className="my-20">
    {interests.length < 1 ? (
      <div className="text-center">
        <h4>No interests created</h4>
      </div>
    ) : (
      interests.map((item) => (
        <InterestCard
          key={item?._id}
          item={item}
          refetchInterests={refetchInterests}
        />
      ))
    )}
  </div>
);

interface InterestCardProps {
  item: any;
  refetchInterests: () => void;
}

const InterestCard: React.FC<InterestCardProps> = ({
  item,
  refetchInterests,
}) => {
  const navigate = useNavigate();
  const handleViewInterestDetails = () => {
    navigate(`/purchase-details/${item._id}`, {
      state: { shownInterest: true, item },
    });
  };

  return (
    <div className="sale-interest-card flex justify-between">
      <div className="flex gap-4">
        <div>
          <h6 className="fs-10">Initiation date</h6>
          <p className="fw-400">{formatDate(item?.createdAt || "")}</p>
        </div>
        {item?.property?.coverImages ||
        item?.property?.otherImages?.length > 0 ? (
          <img
            src={
              item.property.coverImages
                ? item.property.coverImages[0]
                : item?.property?.otherImages[0]
            }
            className=""
            alt="building"
          />
        ) : (
          <div
            className=""
            style={{
              width: "120px",
              height: "100px",
              backgroundColor: "#f3f3f3",
              borderRadius: "10px",
            }}
          ></div>
        )}
        <div>
          <h5>{item.property.propertyName}</h5>
          <h6>
            {item.property.currency.toUpperCase()}&nbsp;
            {numberWithCommas(item.property.price)}
          </h6>
          <PropertyDetails
            bedrooms={item.property.numberOfBedrooms}
            bathrooms={item.property.numberOfBathrooms}
          />
        </div>
      </div>

      <BookInspection item={item} refetchInterests={refetchInterests} />

      <div>
        <button
          onClick={handleViewInterestDetails}
          className="bg-primary text-white rounded-10 "
        >
          View Interest Details
        </button>
      </div>
    </div>
  );
};

const PropertyDetails: React.FC<{ bedrooms: number; bathrooms: number }> = ({
  bedrooms,
  bathrooms,
}) => (
  <div className="flex gap-4">
    <div className="flex gap-2 items-center">
      <i className="fa-regular fa-bed-front text-primary"></i>
      <h6>{bedrooms}</h6>
    </div>
    <div className="flex gap-2 items-center">
      <i className="fa-regular fa-bath text-primary"></i>
      <h6>{bathrooms}</h6>
    </div>
  </div>
);

export default Interests;
